/**
 * Delete my account (#1032, #502 builds 2 and 3): every string, verbatim from
 * `SettingsDeleteAccount.dc.html` (design-round-7/mvp1-gaps, f030d66, sha256
 * 6411e656...) and its fragment's `strings_not_in_app`. A test pins each one;
 * change a word here and the pin fails, so the drawing and the app cannot
 * drift apart quietly.
 *
 * Two bracketed placeholders in the drawing are NOT text and are left out:
 * "[ANYTHING ELSE THAT IS STRIPPED ...]" and "[YOUR DRILLS, THE GUIDES ...]"
 * are waiting on D191.1's legal check.
 *
 * Placeholders that ARE filled: [EMAIL ADDRESS], [PLAYER], [PLAYER COUNT].
 */

export const DELETE_CARD_TITLE = "Delete your account";
export const DELETE_CARD_BODY =
  "Deletes your account for good. The next screen says exactly what goes and what stays, before anything happens.";
export const DELETE_VERB = "Delete my account";
export const DELETE_VERB_BUSY = "Deleting…";
export const DELETE_KEEP = "Keep my account";
export const DELETE_BACK = "Back";

export const DELETE_EYEBROW = "Your account";
export const DELETE_TITLE = "Delete your account";
export const deleteLede = (email) =>
  `This deletes the account signed in as ${email}. Your name and contact details go; your match figures stay, with no name on them. It can't be undone.`;

export const WHAT_GOES_LABEL = "What is deleted";
export const whatGoesLines = (email) => [
  `Your name, ${email}, your mobile number and your photo.`,
  "Your password, your passkeys and every session. You're signed out on every device, and nobody can sign in to this account again.",
  "Your emergency contact's name and phone number.",
  "Your coach links. No coach can see anything of yours any more.",
];

export const WHAT_STAYS_LABEL = "What stays";
export const WHAT_STAYS_LINES = [
  "Your match figures, with no name on them. They are never shown anywhere that names anyone.",
  "Grounds, fields and nets you added to the shared list stay for everyone else, without your name on them.",
  "Usage counts stay. They never carried your name.",
  "Feedback you already sent us stays in our issue tracker as it was filed, with your display name on it.",
  "Payment records are kept by Stripe as the law requires.",
];

export const WHEN_LABEL = "When";
export const WHEN_LINES = [
  "At once, when you press Delete my account.",
  "Backups are kept for 90 days and then expire. Until then they still hold your account as it was.",
];

export const PASSWORD_LABEL = "Your password";
export const PASSWORD_HELPER = "To be sure it's you, not someone holding your phone.";
export const WRONG_PASSWORD = "That password isn't right, so nothing has changed.";

export const CONFIRM_ITS_YOU = "Confirm it's you";
export const USE_A_PASSKEY = "Use a passkey";
export const PASSKEY_CONFIRMED = "Confirmed with your passkey.";
export const EMAIL_ME_A_LINK = "Email me a confirm link";
export const linkSent = (email) =>
  `We've sent a link to ${email}. Open it on this phone to come back here and finish.`;

export const DELETE_FAILED =
  "We couldn't delete your account. Nothing has changed, and you're still signed in. Check your connection and try again.";

/** 502 `delete_billing_stop_failed` (#1566, review of #1592 F2): Stripe could not be reached, so nothing was anonymised. */
export const DELETE_BILLING_STOP_FAILED =
  "We couldn't stop your billing, so nothing was deleted and you're still signed in. Try again in a moment.";

export const DONE_TITLE = "Your account is deleted";
export const DONE_LEDE = "You're signed out on every device. Your name and contact details are gone from the app.";
export const DONE_BACKUPS = "Backups are kept for 90 days and then expire.";
export const DONE_HOME = "Go to the front page";

export const guardianUnder11Lead = (player) =>
  `This is ${player}'s account. You hold its sign-in as their parent or guardian, so you confirm it.`;
export const guardianUnder11Line = (player) =>
  `${player}'s name and contact details go, as listed. Their match figures stay, with no name on them.`;
export const GUARDIAN_11_TO_17 =
  "You're under 18. A parent or guardian should know before you delete your account, just as they should have known when it was made.";
export const coachPlayersLine = (count) =>
  `You coach ${count} ${count === 1 ? "player" : "players"}. Deleting your account ends every link.`;

/**
 * Which way this account confirms, from the facts `GET /auth/me` carries
 * (`has_password`, `passkey_count`, #1032):
 *
 *   - any passkey: "Use a passkey" comes first. A passkey-first account made
 *     before #1032 still reads `has_password: true` (its stored hash is a
 *     real-format one nobody knows), so the passkey is always offered when
 *     one exists (ruling R2, O3).
 *   - a usable password: the password box (under the passkey button when both
 *     are held).
 *   - neither: the emailed confirm link.
 */
export function confirmMethods({ has_password: hasPassword, passkey_count: passkeyCount }) {
  const passkey = Number(passkeyCount) > 0;
  const password = hasPassword === true;
  return { passkey, password, link: !passkey && !password };
}
